<script setup lang="ts">
import type { MagicEmitterEvents } from '@maas/vue-equipment/plugins/MagicEmitter'

import type { NavigationLinkFragment, NavigationTreeFragment } from '#graphql-operations'

interface HeaderNavSubmenuMobileProps {
  nav: NavigationTreeFragment
}

const props = defineProps<HeaderNavSubmenuMobileProps>()

const { unselectView } = useMagicAccordion('mobile-nav')

const activeChild = ref<NavigationTreeFragment | null>(null)

function hasChildren(child: NavigationTreeFragment) {
  return (child.children?.length || 0) > 0
}

function hasNestedChildren(child: NavigationTreeFragment) {
  return child.children?.some(c => (c?.children?.length || 0) > 0)
}

function animateEnter(_el: Element, done: gsap.Callback) {
  gsap.timeline({ defaults: { duration: 0.7, ease: 'circ3' }, onComplete: done })
    .set('.headerNavMobile', { x: 0 })
    .to('.headerNavMobile', { x: '-100vw' })
}

function animateLeave(_el: Element, done: gsap.Callback) {
  gsap.timeline({ defaults: { duration: 0.7, ease: 'circ3' }, onComplete: done })
    .to('.headerNavMobile', { x: 0 })
}

function callback(payload: MagicEmitterEvents['afterLeave']) {
  if (typeof payload === 'string') {
    if (payload === 'navigation-mobile-drawer') {
      activeChild.value = null
      unselectView(props.nav.id as string)
    }
  }
  // else {
  //   if (payload.id === 'mobile-nav') {
  //     console.log('payload.id', payload.id)
  //     unselectView(payload.viewId)
  //   }
  // }
}

const emitter = useMagicEmitter()

emitter.on('afterLeave', callback)

onBeforeUnmount(() => {
  emitter.off('afterLeave', callback)
})
</script>

<template>
  <li class="headerNavSubmenu flex flex-col relative">
    <MagicAccordionView :id="(nav.id as string)" class="flex flex-col">
      <div class="inline-flex">
        <MagicAccordionTrigger as-child>
          <ButtonNav :title="nav.title">
            <template #icon>
              <i class="i-icon-chevron-down motion-base flex-none size-6 icon" />
            </template>
          </ButtonNav>
        </MagicAccordionTrigger>
      </div>
      <MagicAccordionContent>
        <ul class="py-4 grid space-y-2">
          <li v-for="child in (nav.children as NavigationTreeFragment[])" :key="child.id">
            <ButtonNavSubmenu
              v-if="hasChildren(child)"
              :title="child.title"
              @click="activeChild = child"
            >
              <template #icon>
                <i class="i-icon-arrow-right flex-none size-6 icon" />
              </template>
            </ButtonNavSubmenu>
            <ButtonNavSubmenu v-else :link="(child as NavigationLinkFragment)" />
          </li>
        </ul>
      </MagicAccordionContent>
    </MagicAccordionView>

    <Transition @enter="animateEnter" @leave="animateLeave">
      <nav v-show="activeChild" class="submenu size-full left-100vw top-0 absolute z-1">
        <div class="space-y-4">
          <button class="inline-flex gap-2 items-center justify-center" @click.stop.prevent="activeChild = null">
            <i class="i-icon-arrow-left size-6 icon left-0 absolute" />
            <Text class="typo-sans-1 ml-8 uppercase capsize" :text="`${$t('back_to')} ${nav.title}`" />
          </button>
          <p v-if="activeChild" class="typo-serif-1 c-$accent-color mb-6 underline block">
            {{ activeChild.title }}
          </p>
        </div>
        <SubmenuMobileNested
          v-if="activeChild && hasNestedChildren(activeChild)"
          :nav="activeChild"
          :children="(activeChild.children as NavigationTreeFragment[])"
        />
        <SubmenuMobileSimple
          v-else-if="activeChild"
          :children="(activeChild.children as NavigationLinkFragment[])"
        />
        <ButtonNav
          v-if="activeChild"
          class="mt-10"
          :title="activeChild.badge"
          :link="activeChild"
        >
          <template #icon>
            <i class="i-icon-arrow-right flex-none size-6 icon" />
          </template>
        </ButtonNav>
      </nav>
    </Transition>
  </li>
</template>

<style scoped>
:deep([data-active='true'] .buttonNav) .icon {
  transform: rotate(-180deg);
}
</style>
